import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { WorshipService } from '../../shared/api/types';
import { Alert, Button, Card, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime } from '../calendar/dates';
import { QrCode } from '../nursery/QrCode';

const checkInUrl = (token: string) => `${window.location.origin}/app/asistencia/qr/${token}`;

/** Para el equipo: el QR del culto, para proyectarlo o imprimirlo en la entrada. */
export function QrCard({ churchId, serviceId, title }: { churchId: string; serviceId: string; title: string }) {
  const qr = useAsync((signal) => api.get<{ token: string }>(`/attendance/churches/${churchId}/services/${serviceId}/qr`, signal), [churchId, serviceId]);
  const box = useRef<HTMLDivElement>(null);
  if (qr.status === 'loading') return null;
  if (qr.status === 'error') return <ErrorState error={qr.error} onRetry={qr.reload} />;
  const url = checkInUrl(qr.data.token);
  return (
    <Card title="Asistencia con QR">
      <p className="small">
        Proyéctalo o imprímelo en la entrada. Cada persona lo escanea con la cámara de su celular y queda registrada (desde una hora antes hasta una hora después del culto).
      </p>
      <div ref={box} className="qr-attendance">
        <p className="qr-attendance__title">{title}</p>
        <QrCode value={url} label={`Código QR de asistencia: ${title}`} />
        <p className="qr-attendance__hint">Escanea para registrar tu asistencia</p>
      </div>
      <div className="actions">
        <Button variant="secondary" onClick={() => void box.current?.requestFullscreen?.().catch(() => undefined)}>
          Proyectar en pantalla completa
        </Button>
        <Button variant="ghost" onClick={() => window.print()}>
          Imprimir
        </Button>
      </div>
    </Card>
  );
}

type CheckIn = { churchId: string; occasion: WorshipService; alreadyRecorded: boolean };

/** Quien escanea el QR del culto llega aquí (con sesión) y queda registrado. */
export function QrCheckInPage() {
  const { token = '' } = useParams();
  const [result, setResult] = useState<{ ok: CheckIn } | { error: string } | null>(null);
  const sent = useRef('');
  useEffect(() => {
    if (sent.current === token) return;
    sent.current = token;
    api
      .post<CheckIn>(`/attendance/qr/${token}/check-in`)
      .then((ok) => setResult({ ok }))
      .catch((e) => setResult({ error: errorMessage(e) }));
  }, [token]);

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Asistencia</h1>
      </header>
      {!result ? <Loading /> : null}
      {result && 'ok' in result ? (
        <Card>
          <p className="qr-checkin__ok">{result.ok.alreadyRecorded ? 'Tu asistencia ya estaba registrada.' : '¡Listo! Registramos tu asistencia.'}</p>
          <p>
            <strong>{result.ok.occasion.title}</strong>
            <br />
            <span className="muted">
              {formatDayHeading(new Date(result.ok.occasion.startsAt))} · {formatTime(result.ok.occasion.startsAt)}
            </span>
          </p>
          <p className="muted">¡Bienvenido! Que Dios te bendiga en este tiempo.</p>
          <Link className="btn btn--secondary" to="/app">
            Ir al inicio
          </Link>
        </Card>
      ) : null}
      {result && 'error' in result ? (
        <Card>
          <Alert>{result.error}</Alert>
          <p className="small muted">Si eres visita o aún no perteneces a esta iglesia, pide a un ujier que registre tu asistencia.</p>
          <Link className="btn btn--secondary" to="/app">
            Ir al inicio
          </Link>
        </Card>
      ) : null}
    </div>
  );
}
